import { useState, useEffect, useRef } from "react";
import actions from "../config.json";
import passionMap from "../pages/OffersLandingPage/generatedPassionMap.json";
import { useSearchStore } from "./useSearchStore";
import { useOfferBasicData } from "./useOfferBasicData";
import { ENV } from "../env";
import useToast from "../components/Toast/useToast";
import { generateMarriottReservationUrl } from "../utils/generateMarriottReservationUrl";

const MAX_PROPERTIES = 12;

/**
 * Format a date object from the date range picker to YYYY-MM-DD format for API
 * @param {Object} date - Date object from the date range picker
 * @returns {string} - Formatted date string
 */
const formatDateForAPI = (date) => {
  if (!date || !date.year || !date.month || !date.day) return "";
  return `${date.year}-${date.month.toString().padStart(2, "0")}-${date.day.toString().padStart(2, "0")}`;
};

/**
 * Categorize and limit properties based on passion map
 * @param {Array} properties - Array of property objects or IDs
 * @param {string} passion - The passion type to filter by
 * @returns {Object} Object with passionProperties and nonPassionProperties arrays
 */
const categorizeProperties = (properties, passion) => {
  const passionIds = passionMap[passion || "beach"];
  const passionProperties = [];
  const nonPassionProperties = [];

  properties.forEach((property) => {
    const id = property.id || property;
    const propertyObj = typeof property === "object" ? property : { id };
    const isPassion = passionIds.includes(id);
    const enrichedProperty = { ...propertyObj, isPassion };

    if (isPassion) {
      passionProperties.push(enrichedProperty);
    } else {
      nonPassionProperties.push(enrichedProperty);
    }
  });

  return { passionProperties, nonPassionProperties };
};

/**
 * Get limited properties prioritizing passion properties
 * @param {Array} passionProperties - Passion properties
 * @param {Array} nonPassionProperties - Non-passion properties
 * @returns {Array} Limited array of properties
 */
const getLimitedProperties = (
  passionProperties,
  nonPassionProperties,
  maxOverride,
) => {
  if (passionProperties.length >= (maxOverride || MAX_PROPERTIES)) {
    return passionProperties.slice(0, maxOverride || MAX_PROPERTIES);
  }

  const remainingCount =
    (maxOverride || MAX_PROPERTIES) - passionProperties.length;
  return [
    ...passionProperties,
    ...nonPassionProperties.slice(0, remainingCount),
  ];
};

/**
 * Hook to fetch and manage hotel list data
 * @param {string} offerId - The offer ID to fetch hotels for
 * @returns {Object} - Hotel data and loading state
 */
export const useOfferData = (offerId, passion, skipInitialLoad) => {
  const [offerData, setOfferData] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [wasSearchPerformed, setWasSearchPerformed] = useState(false);
  const [paginationData, setPaginationData] = useState({
    processedIds: [],
    remainingIds: [],
    hasMoreData: false,
    totalProcessed: 0,
    totalRemaining: 0,
  });
  const { rooms, adults, children } = useSearchStore();
  const { showErrorToast } = useToast();
  const offersBasicDataRef = useRef(null);

  // Use the basic data hook for passion info and property IDs
  const {
    offerBasicData,
    loading: basicDataLoading,
    error: basicDataError,
  } = useOfferBasicData(offerId, passion);

  useEffect(() => {
    offersBasicDataRef.current = offerBasicData;
  }, [offerBasicData]);

  /**
   * Fetch the property details using the basic data
   */
  const fetchPropertyDetails = async (
    idsToFetch = null,
    appendResults = false,
  ) => {
    const targetIds =
      idsToFetch ||
      offersBasicDataRef.current?.propertiesToFetch?.map((p) => p.id);

    if (!targetIds?.length) {
      return;
    }

    setWasSearchPerformed(true);

    setPropertiesLoading(true);
    setError(null);

    // Clear existing properties when not appending (fresh search)
    if (!appendResults) {
      setOfferData((prevData) => ({
        ...prevData,
        hotProperties: { hotels: [] },
      }));
    }

    try {
      const { rooms, adults, children } = useSearchStore.getState();

      const actionUrl = ENV.FEATURE_FLAGS.useMbopQueries
        ? "marriott-passions-v3/get-offer-hotels-by-id-mbop"
        : "marriott-passions-v3/get-offer-hotels-by-id";
      const hotelsDetailUrl = actions[actionUrl];
      const hotelsResponse = await fetch(`${hotelsDetailUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ids: targetIds,
          offerId,
          // Get date range from the search store and format for API
          startDate: formatDateForAPI(
            useSearchStore.getState().dateRange.start,
          ),
          endDate: formatDateForAPI(useSearchStore.getState().dateRange.end),
          rooms,
          adults,
          children,
        }),
      });

      if (!hotelsResponse.ok) {
        throw new Error(
          `Error fetching hotel details: ${hotelsResponse.status}`,
        );
      }

      const hotelsData = await hotelsResponse.json();

      // Update pagination data with the new response structure
      setPaginationData({
        processedIds: hotelsData.processedIds || [],
        remainingIds: hotelsData.remainingIds || [],
        hasMoreData: hotelsData.hasMoreData || false,
        totalProcessed: hotelsData.totalProcessed || 0,
        totalRemaining: hotelsData.totalRemaining || 0,
      });

      // Add isPassion flag to the returned hotels for UI display
      const propertyMap = new Map(
        offersBasicDataRef.current.propertiesToFetch.map((p) => [
          p.id,
          p.isPassion,
        ]),
      );

      const processedHotels = hotelsData?.properties
        ?.map((hotel) => ({
          ...hotel,
          isPassion: propertyMap.get(hotel.id) || false,
        }))
        .sort((a, b) => b.isPassion - a.isPassion);

      const finalData = {
        ...offersBasicDataRef.current,
        hotProperties: {
          hotels: appendResults
            ? [...(offerData?.hotProperties?.hotels || []), ...processedHotels]
            : processedHotels,
        },
        timestamp: Date.now(),
      };

      setOfferData(finalData);
      setPropertiesLoading(false);
    } catch (err) {
      console.error("Error fetching property details:", err);
      setError(err.message || "Failed to fetch property details");
      setPropertiesLoading(false);
      showErrorToast(
        "Failed to fetch offers. Please update your search criteria or try again.",
      );
    }
  };

  /**
   * Load more properties for pagination (ID-based search)
   */
  const loadMoreProperties = async () => {
    if (
      !paginationData.hasMoreData ||
      paginationData.remainingIds.length === 0
    ) {
      console.log("No more properties to load");
      return;
    }

    console.log(
      `Loading more properties. Remaining: ${paginationData.remainingIds.length}`,
    );
    await fetchPropertyDetails(paginationData.remainingIds, true);
  };

  /**
   * Load more properties for location-based pagination
   */
  const loadMoreLocationProperties = async (searchParams) => {
    if (!paginationData.hasMoreData || !paginationData.nextOffset) {
      console.log("No more properties to load for location search");
      return;
    }

    setPropertiesLoading(true);

    try {
      const { dateRange } = useSearchStore.getState();
      const startDate = dateRange.start;
      const endDate = dateRange.end;

      const formattedParams = {
        startDate: `${startDate.year}-${startDate.month.toString().padStart(2, "0")}-${startDate.day.toString().padStart(2, "0")}`,
        endDate: `${endDate.year}-${endDate.month.toString().padStart(2, "0")}-${endDate.day.toString().padStart(2, "0")}`,
        rooms: searchParams.rooms,
        adults: searchParams.guests,
        children: searchParams.children || [],
        latitude: searchParams.selectedLocation?.latitude,
        longitude: searchParams.selectedLocation?.longitude,
        city: searchParams.selectedLocation?.city,
        state: searchParams.selectedLocation?.state,
        country: searchParams.selectedLocation?.country,
        offerId: offerId,
        offset: paginationData.nextOffset,
        limit: 12,
      };

      const actionUrl = ENV.FEATURE_FLAGS.useMbopQueries
        ? "marriott-passions-v3/get-offer-hotels-by-location-mbop"
        : "marriott-passions-v3/get-offer-hotels-by-location";
      const hotelsByLocationUrl = actions[actionUrl];
      const response = await fetch(`${hotelsByLocationUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedParams),
      });

      if (!response.ok) {
        throw new Error(`Error fetching more hotels: ${response.status}`);
      }

      const responseData = await response.json();

      // Update pagination data with new offset
      setPaginationData({
        processedIds: [],
        remainingIds: [],
        hasMoreData: responseData.hasMoreData || false,
        totalProcessed:
          (paginationData.totalProcessed || 0) +
          (responseData.totalReturned || 0),
        totalRemaining: 0,
        nextOffset: responseData.nextOffset || null,
      });

      const { passionProperties, nonPassionProperties } = categorizeProperties(
        responseData.properties,
        passion,
      );

      const newProperties = [
        ...passionProperties,
        ...nonPassionProperties,
      ].sort((a, b) => b.isPassion - a.isPassion);

      // Append new properties to existing ones
      setOfferData((prevData) => ({
        ...prevData,
        hotProperties: {
          hotels: [
            ...(prevData?.hotProperties?.hotels || []),
            ...newProperties,
          ],
        },
      }));

      setPropertiesLoading(false);

      console.log(
        `Loaded ${newProperties.length} more properties. Total: ${(offerData?.hotProperties?.hotels?.length || 0) + newProperties.length}`,
      );
    } catch (err) {
      console.error("Error loading more location properties:", err);
      setError(err.message || "Failed to load more properties");
      setPropertiesLoading(false);
    }
  };

  /**
   * Fetch the complete offer data (for backward compatibility)
   */
  const fetchOfferData = async () => {
    // This will trigger the basic data fetch, which will then trigger property details
    if (offersBasicDataRef.current) {
      await fetchPropertyDetails();
    }
  };

  const handleSearchChange = async (params) => {
    setPropertiesLoading(true);
    setError(null);

    // Clear existing properties to show loading state properly
    setOfferData((prevData) => ({
      ...prevData,
      hotProperties: { hotels: [] },
    }));

    setWasSearchPerformed(true);

    try {
      const { dateRange } = useSearchStore.getState();
      const startDate = dateRange.start;
      const endDate = dateRange.end;

      const formattedParams = {
        startDate: `${startDate.year}-${startDate.month.toString().padStart(2, "0")}-${startDate.day.toString().padStart(2, "0")}`,
        endDate: `${endDate.year}-${endDate.month.toString().padStart(2, "0")}-${endDate.day.toString().padStart(2, "0")}`,
        rooms: params.rooms,
        adults: params.guests,
        children: params.children || [],
        latitude: params.selectedLocation?.latitude,
        longitude: params.selectedLocation?.longitude,
        city: params.selectedLocation?.city,
        state: params.selectedLocation?.state,
        country: params.selectedLocation?.country,
        offerId: offerId,
        offset: 0,
        limit: 12,
      };

      const actionUrl = ENV.FEATURE_FLAGS.useMbopQueries
        ? "marriott-passions-v3/get-offer-hotels-by-location-mbop"
        : "marriott-passions-v3/get-offer-hotels-by-location";
      const hotelsByLocationUrl = actions[actionUrl];
      const response = await fetch(`${hotelsByLocationUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedParams),
      });

      if (!response.ok) {
        throw new Error(`Error fetching hotels: ${response.status}`);
      }

      const responseData = await response.json();

      // Update pagination data from location-based search
      setPaginationData({
        processedIds: [],
        remainingIds: [],
        hasMoreData: responseData.hasMoreData || false,
        totalProcessed: responseData.totalReturned || 0,
        totalRemaining: 0,
        nextOffset: responseData.nextOffset || null,
      });

      const { passionProperties, nonPassionProperties } = categorizeProperties(
        responseData.properties,
        passion,
      );

      // If searching by state only (without specific coordinates), show all results
      const isStateOnlySearch =
        params.selectedLocation?.state &&
        !params.selectedLocation?.latitude &&
        !params.selectedLocation?.longitude &&
        !params.selectedLocation?.city;

      const finalProperties = isStateOnlySearch
        ? [...passionProperties, ...nonPassionProperties].sort(
            (a, b) => b.isPassion - a.isPassion,
          )
        : getLimitedProperties(passionProperties, nonPassionProperties);

      setOfferData((prevData) => ({
        ...prevData,
        hotProperties: { hotels: finalProperties },
      }));
      setPropertiesLoading(false);

      return finalProperties;
    } catch (err) {
      console.error("Error in handleSearchChange:", err);
      setError(err.message || "Failed to search properties");
      setPropertiesLoading(false);
      return [];
    }
  };

  const bookNow = async (propertyData) => {
    // Generate reservation URL with the property data
    try {
      // Get dates from search store
      const { dateRange } = useSearchStore.getState();

      // Format dates from the search store to MM-DD-YYYY format
      const startDateParam = `${dateRange.start.month.toString().padStart(2, "0")}-${dateRange.start.day.toString().padStart(2, "0")}-${dateRange.start.year}`;
      const endDateParam = `${dateRange.end.month.toString().padStart(2, "0")}-${dateRange.end.day.toString().padStart(2, "0")}-${dateRange.end.year}`;

      // Function to convert MM-DD-YYYY to MM/DD/YYYY format for Marriott URL
      const convertToMarriottFormat = (dateStr) => {
        if (!dateStr) return null;

        const mmDdYyyyRegex =
          /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-([0-9]{4})$/;
        if (mmDdYyyyRegex.test(dateStr)) {
          const [month, day, year] = dateStr.split("-");
          return `${month}/${day}/${year}`;
        }
        return null;
      };

      // Use formatted dates from the search store
      const fromDate = convertToMarriottFormat(startDateParam);
      const toDate = convertToMarriottFormat(endDateParam);

      // Convert children array to ages string
      const childrenAges = children
        .map((age) => (age === 0 ? "<1" : age.toString()))
        .join(",");

      const reservationUrl = generateMarriottReservationUrl({
        marrOfferId: offerId,
        fromDate: fromDate,
        toDate: toDate,
        propertyCode: propertyData.id,
        numberOfRooms: rooms,
        numberOfAdults: adults,
        childrenCount: children.length,
        childrenAges: childrenAges,
        clusterCode: offerData?.clusterCode,
      });

      // Open the reservation URL in a new window
      window.open(reservationUrl, "_blank");
    } catch (error) {
      console.error("Error generating reservation URL:", error);
    }
  };

  // Fetch property details when basic data is available or when offerId/passion changes
  useEffect(() => {
    if (offerBasicData) {
      if (skipInitialLoad) {
        setOfferData((prevData) => ({
          ...offerBasicData,
          ...prevData,
        }));
      } else {
        fetchPropertyDetails();
      }
    }
  }, [offerBasicData, offerId, passion, skipInitialLoad]);

  // Handle errors from basic data
  useEffect(() => {
    if (basicDataError) {
      setError(basicDataError);
    }
  }, [basicDataError]);

  return {
    offerData: offerData || offersBasicDataRef.current,
    loading: basicDataLoading || propertiesLoading,
    basicDataLoading,
    propertiesLoading,
    error,
    handleSearchChange,
    refetch: fetchOfferData,
    bookNow,
    fetchOfferData,
    // Pagination support
    paginationData,
    loadMoreProperties,
    loadMoreLocationProperties,
    hasMoreData: paginationData.hasMoreData,
    wasSearchPerformed,
    setWasSearchPerformed,
  };
};
